import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { HomeIcon, CheckCircleIcon, SendIcon, Loader2Icon, MapPinIcon, ClipboardListIcon, FileTextIcon, HardHatIcon, KeyIcon, MessageCircleIcon } from 'lucide-react';
import { ContactSection } from '../components/ContactSection';
import { useLanguage } from '../contexts/LanguageContext';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
type FormData = {
  nombre: string;
  email: string;
  telefono: string;
  ubicacion: string;
  metros: string;
  tipoProyecto: string;
  plazo: string;
  detalles: string;
};
export function ConstructionPage() {
  const {
    t
  } = useLanguage();
  const homeTypes = [{
    title: t('construction.type.modern'),
    description: t('construction.desc.modern'),
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80'
  }, {
    title: t('construction.type.rustic'),
    description: t('construction.desc.rustic'),
    image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80'
  }, {
    title: t('construction.type.mediterranean'),
    description: t('construction.desc.mediterranean'),
    image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80'
  }, {
    title: t('construction.type.minimalist'),
    description: t('construction.desc.minimalist'),
    image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&q=80'
  }, {
    title: t('construction.type.traditional'),
    description: t('construction.desc.traditional'),
    image: 'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=800&q=80'
  }, {
    title: t('construction.type.contemporary'),
    description: t('construction.desc.contemporary'),
    image: 'https://images.unsplash.com/photo-1600573472592-401b489a3cdc?w=800&q=80'
  }, {
    title: t('construction.type.country'),
    description: t('construction.desc.country'),
    image: 'https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=800&q=80'
  }, {
    title: t('construction.type.farmhouse'),
    description: t('construction.desc.farmhouse'),
    image: 'https://images.unsplash.com/photo-1600210492493-0946911123ea?w=800&q=80'
  }];
  const processSteps = [{
    icon: ClipboardListIcon,
    title: t('page.construction.process.step1.title'),
    desc: t('page.construction.process.step1.desc')
  }, {
    icon: FileTextIcon,
    title: t('page.construction.process.step2.title'),
    desc: t('page.construction.process.step2.desc')
  }, {
    icon: FileTextIcon,
    title: t('page.construction.process.step3.title'),
    desc: t('page.construction.process.step3.desc')
  }, {
    icon: HardHatIcon,
    title: t('page.construction.process.step4.title'),
    desc: t('page.construction.process.step4.desc')
  }, {
    icon: KeyIcon,
    title: t('page.construction.process.step5.title'),
    desc: t('page.construction.process.step5.desc')
  }];
  const [formData, setFormData] = useState<FormData>({
    nombre: '',
    email: '',
    telefono: '',
    ubicacion: '',
    metros: '',
    tipoProyecto: '',
    plazo: '',
    detalles: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    setIsSubmitted(true);
  };
  return <div className="w-full min-h-screen bg-white" data-id="element-339">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] w-full bg-black overflow-hidden pt-20" data-id="element-340">
        <div className="absolute inset-0 z-0" data-id="element-341">
          <img src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&q=80" alt="Construction" className="w-full h-full object-cover opacity-50" data-id="element-342" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" data-id="element-343" />
        </div>

        <div className="relative z-10 flex flex-col items-center justify-center min-h-[70vh] px-6 py-24" data-id="element-344">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="max-w-4xl mx-auto text-center" data-id="element-345">
            <motion.div className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 backdrop-blur-sm rounded-full text-white font-medium border border-white/10 mb-8" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} data-id="element-346">
              <MapPinIcon className="w-4 h-4" data-id="element-347" />
              {t('hero.badge')}
            </motion.div>

            <motion.h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 drop-shadow-xl" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} data-id="element-348">
              <span className="text-white" data-id="element-349">
                {t('page.construction.hero.title1')}
              </span>
              <br data-id="element-350" />
              <span className="bg-gradient-to-r from-white via-neutral-200 to-neutral-400 bg-clip-text text-transparent" data-id="element-351">
                {t('page.construction.hero.title2')}
              </span>
            </motion.h1>

            <motion.p className="text-lg md:text-xl text-neutral-300 max-w-2xl mx-auto leading-relaxed font-medium" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} data-id="element-352">
              {t('page.construction.hero.subtitle')}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Home Types Section */}
      <section className="w-full bg-white py-28 px-6" data-id="element-353">
        <div className="max-w-7xl mx-auto" data-id="element-354">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="mb-16" data-id="element-355">
            <div className="flex items-center gap-3 mb-4" data-id="element-356">
              <div className="w-14 h-14 bg-black rounded-2xl flex items-center justify-center shadow-lg" data-id="element-357">
                <HomeIcon className="w-7 h-7 text-white" data-id="element-358" />
              </div>
              <span className="text-sm font-bold tracking-widest text-neutral-500 uppercase" data-id="element-359">
                {t('construction.label')}
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6" data-id="element-360">
              {t('construction.title')}
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl leading-relaxed" data-id="element-361">
              {t('construction.subtitle')}
            </p>
          </motion.div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-362">
            {homeTypes.map((home, index) => <motion.div key={index} variants={itemVariants} className="group bg-neutral-50 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500" whileHover={{
            y: -8
          }} data-id="element-363">
                <div className="relative h-64 overflow-hidden" data-id="element-364">
                  <motion.img src={home.image} alt={home.title} className="w-full h-full object-cover saturate-[0.8] group-hover:saturate-[1.1] transition-all duration-700" whileHover={{
                scale: 1.1
              }} data-id="element-365" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-366" />
                </div>
                <div className="p-6" data-id="element-367">
                  <h3 className="text-lg font-bold text-black mb-3" data-id="element-368">
                    {home.title}
                  </h3>
                  <p className="text-neutral-600 text-sm leading-relaxed" data-id="element-369">
                    {home.description}
                  </p>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="w-full bg-neutral-50 py-28 px-6" data-id="element-370">
        <div className="max-w-7xl mx-auto" data-id="element-371">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="text-center mb-16" data-id="element-372">
            <span className="text-sm font-bold tracking-widest text-neutral-500 uppercase mb-4 block" data-id="element-373">
              {t('page.construction.process.label')}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-black" data-id="element-374">
              {t('page.construction.process.title')}
            </h2>
          </motion.div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid md:grid-cols-5 gap-6" data-id="element-375">
            {processSteps.map((step, index) => <motion.div key={index} variants={itemVariants} className="relative text-center" data-id="element-376">
                <motion.div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg" whileHover={{
              scale: 1.1,
              rotate: 5
            }} data-id="element-377">
                  <step.icon className="w-7 h-7 text-white" data-id="element-378" />
                </motion.div>
                <div className="absolute top-8 left-[60%] right-0 h-0.5 bg-neutral-200 hidden md:block last:hidden" style={{
              width: 'calc(100% - 32px)'
            }} data-id="element-379" />
                <h3 className="font-bold text-black mb-2" data-id="element-380">{step.title}</h3>
                <p className="text-sm text-neutral-600" data-id="element-381">{step.desc}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Form Section - REPLACED WITH WHATSAPP CTA */}
      <section className="w-full bg-black py-28 px-6 relative overflow-hidden" data-id="element-382">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-neutral-900 to-transparent pointer-events-none" data-id="element-383" />

        <div className="max-w-3xl mx-auto relative z-10 text-center" data-id="element-384">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} data-id="element-385">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-386">
              ¿Listo para empezar tu proyecto?
            </h2>
            <p className="text-neutral-400 mb-12 text-lg max-w-2xl mx-auto" data-id="element-387">
              Contáctanos directamente por WhatsApp para recibir asesoramiento
              personalizado y un presupuesto sin compromiso.
            </p>

            <motion.a href="https://wa.me/34631012592?text=Hola,%20me%20gustaría%20solicitar%20información%20sobre%20construcción%20de%20casas" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 px-10 py-5 bg-[#25D366] text-white font-bold text-xl rounded-full transition-all duration-300 hover:bg-[#20bd5a] hover:shadow-lg hover:shadow-[#25D366]/20" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} data-id="element-388">
              <MessageCircleIcon className="w-8 h-8" data-id="element-389" />
              Contactar por WhatsApp
            </motion.a>

            <div className="mt-8 text-neutral-500" data-id="element-390">
              <p className="text-sm" data-id="element-391">
                O llámanos al{' '}
                <a href="tel:+34631012592" className="text-white hover:underline" data-id="element-392">
                  +34 631 01 25 92
                </a>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <ContactSection data-id="element-393" />
    </div>;
}